// src/pages/Login.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { updateFavicon, updatePageTitle } from "../utils/favicon";
import apiService from "../services/apiService";
import "./styles/Login.css";

const isDev = process.env.NODE_ENV !== "production";

function Login() {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();
  const { login, user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const isLoggingIn = useRef(false); // prevent multiple concurrent login attempts

  // Redirect to home once auth state resolves and a user is present.
  useEffect(() => {
    if (!authLoading && user) {
      navigate("/home", { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Update favicon/title when organization data changes.
  useEffect(() => {
    const organization = user?.user?.organization;
    if (organization) {
      if (organization.favicon_url) updateFavicon(organization.favicon_url);
      if (organization.name) updatePageTitle(organization.name, "Login");
    }
  }, [user?.user?.organization]);

  const handleLogin = async () => {
    // Guard against double-submits and against starting a new popup while
    // MSAL is already mid-interaction (e.g. a redirect is being processed).
    if (isLoggingIn.current || isLoading || inProgress !== InteractionStatus.None) {
      return;
    }

    if (!termsAccepted) {
      setError("Please accept the Terms of Use and Privacy Policy");
      return;
    }

    isLoggingIn.current = true;
    setIsLoading(true);
    setError("");

    try {
      const loginResponse = await instance.loginPopup({
        scopes: ["openid", "profile", "email"],
      });

      if (!loginResponse?.idToken) {
        setError("Login cancelled or failed. Please try again.");
        return;
      }

      const response = await apiService.loginWithMicrosoft(loginResponse.idToken);

      if (response.status === 200 && response.data) {
        const organization = response.data.user?.organization;
        if (organization?.favicon_url) updateFavicon(organization.favicon_url);
        if (organization?.name) updatePageTitle(organization.name);

        // Sets user + schedules token refresh. Navigation happens via the
        // useEffect above once `user` updates — no manual navigate() needed.
        login(response.data);
      } else {
        setError("Authentication failed. Please try again.");
      }
    } catch (err) {
      if (isDev) console.error("Login error:", err);

      // Map to safe, generic messages — never show raw backend/MSAL error
      // text directly to the user (avoids leaking internal details).
      const msg = err?.message || "";
      if (msg.includes("cancel") || err?.errorCode === "user_cancelled") {
        setError("Login was cancelled. Please try again.");
      } else if (msg.includes("popup") || err?.errorCode === "popup_window_error") {
        setError("Popup was blocked. Please allow popups for this site and try again.");
      } else if (msg.toLowerCase().includes("network")) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
      isLoggingIn.current = false;
    }
  };

  if (authLoading) {
    return (
      <div className="login-container">
        <div className="login-loading">Loading...</div>
      </div>
    );
  }

  if (user) return null; // will redirect via useEffect

  return (
    <div className="login-container">
      <div className="gradient-bg"></div>
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>

      <div className="login-content">
        <div className="login-card">
          <div className="brand-section">
            <h1 className="brand-title">Welcome Back</h1>
            <p className="brand-subtitle">Sign in to continue to your account</p>
          </div>

          {error && (
            <div className="error-message" role="alert">
              {error}
            </div>
          )}

          <div className="terms-acceptance">
            <label className="terms-label">
              <input
                type="checkbox"
                className="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>

          <button
            className={`login-button ${isLoading ? "loading" : ""} ${!termsAccepted ? "disabled" : ""}`}
            onClick={handleLogin}
            disabled={isLoading || !termsAccepted}
            title={!termsAccepted ? "Please accept the Terms of Use and Privacy Policy to continue" : ""}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              <>Log in</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Login;